"use client";

import { useClarityAnalytics, useUmamiAnalytics } from "@analytics";
import { useLocalePathname } from "@i18n/routing";
import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { normalizePage } from "@saas/payments/lib/track-page";
import { readAbVariant } from "@shared/hooks/use-ab-variant";
import { fireConfettiOncePerSession } from "@shared/lib/confetti";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { ShieldCheckIcon } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import { WechatPayLogo } from "./WechatPayLogo";

const PricingTable = dynamic(
	() => import("./PricingTable").then((mod) => mod.PricingTable),
	{
		ssr: false,
		loading: () => <div className="min-h-[240px] w-full" />,
	},
);

export type CreditsPaywallReason =
	| "insufficient-credits"
	| "nano-banana-pro"
	/** 其他付费专属图片模型（qwen3 pro / seedream 5 pro），用通用文案 */
	| "paid-model"
	| "watermark"
	| "private"
	/** 图片的 2K/4K：二元闸门，任意付费（含积分包）即解锁 */
	| "image-resolution"
	/** 视频的 720p/1080p/4K：按档位切，720p 起 Starter、1080p 起 Pro */
	| "video-resolution"
	/** 没付过钱的用户每次生成成功后弹，见 use-post-generation-paywall.ts */
	| "post-generation"
	/** AI 音乐的 WAV 下载和商用授权：任意付费（含积分包）即解锁 */
	| "music-wav";

interface CreditsPaywallModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Controls the header copy. Defaults to the out-of-credits message. */
	reason?: CreditsPaywallReason | null;
}

/**
 * Shared paywall shown when a user runs out of credits (or hits a premium
 * gate). Embeds the full PricingTable so the modal always shows live prices
 * and routes "Choose this plan" straight to checkout — no extra page hop.
 */
export function CreditsPaywallModal({
	open,
	onOpenChange,
	reason = "insufficient-credits",
}: CreditsPaywallModalProps) {
	const t = useTranslations();
	const locale = useLocale();
	const { user } = useSession();
	const { activePlan } = usePurchases();
	const { trackEvent } = useUmamiAnalytics();
	const { trackEvent: trackClarityEvent } = useClarityAnalytics();
	const pathname = useLocalePathname();
	const { credits, getRemainingCredits, getTotalCredits } = useCredits();
	// 注册国家不发免费额度的账号（config/free-credit-regions.ts）：余额是 0 但
	// 不是「用完了」，墙上要说清楚为什么，不然用户以为注册坏了。
	const isRegionBlocked =
		!!credits?.IMAGE_GENERATION?.freeGrantBlockedByRegion;

	// Track each time the paywall opens so we can measure the funnel
	// (paywall-viewed → checkout-clicked) per trigger reason and per page.
	// variant carries the CTA-copy bucket so both ends of that funnel can be
	// split by it; read straight from the cookie to keep this effect's deps
	// stable, so one paywall open stays one event.
	useEffect(() => {
		if (open) {
			void trackEvent("paywall-viewed", {
				reason: reason ?? "insufficient-credits",
				page: normalizePage(pathname),
				variant: readAbVariant("ab_cta"),
			});
			trackClarityEvent("paywall-viewed");
		}
	}, [open, reason, pathname, trackEvent, trackClarityEvent]);

	// Running out of credits is the moment the user has invested the most —
	// Clarity puts this cohort at 2.2× the page count of everyone else. A small
	// burst plus the milestone line below reframes the wall as "look how far
	// you got" instead of "you are blocked". Kept to one burst per tab session
	// so repeat walls in the same visit don't turn into confetti spam.
	useEffect(() => {
		if (open && reason === "insufficient-credits") {
			void fireConfettiOncePerSession("paywall-confetti", {
				particleCount: 40,
				spread: 55,
				startVelocity: 28,
				origin: { y: 0.3 },
			});
		}
	}, [open, reason]);

	// Premium feature gates (private mode, high resolution, …) are not a
	// credit shortage — the user may still have plenty of credits — so they get
	// a generic upgrade message instead of the "out of credits" copy. The
	// watermark gate is the most common of them (~800 views/month) and gets
	// its own copy naming exactly what upgrading unlocks.
	const isNanoBananaPro = reason === "nano-banana-pro";
	const isWatermark = reason === "watermark";
	// 图片和视频的分辨率闸门规则不同，文案不能共用：图片买个积分包就解锁 4K，
	// 视频的 1080p 必须订阅 Pro。混用会把图片用户引去买不需要的高档订阅。
	const isVideoResolution = reason === "video-resolution";
	const isImageResolution = reason === "image-resolution";
	const isPremiumFeature = reason === "private" || reason === "paid-model";
	// 免费视频本来就没水印，所以文案里的「无水印」只说图片
	const isPostGeneration = reason === "post-generation";
	const isMusicWav = reason === "music-wav";

	const copy = isMusicWav
		? {
				title: t("imageGenerator.upgradeModal.musicWavTitle", {
					defaultValue: "Upgrade for WAV and Commercial Use",
				}),
				description: t(
					"imageGenerator.upgradeModal.musicWavDescription",
					{
						defaultValue:
							"Lossless WAV downloads and a commercial-use license for your songs come with any paid plan or one-time credit pack.",
					},
				),
			}
		: isPostGeneration
			? {
					title: t(
						"imageGenerator.upgradeModal.postGenerationTitle",
						{
							defaultValue: "Love What You Made? Keep Creating",
						},
					),
					description: t(
						"imageGenerator.upgradeModal.postGenerationDescription",
						{
							defaultValue:
								"Free credits run out fast. Grab a one-time credit pack or subscribe for more credits, higher resolution and watermark-free images.",
						},
					),
				}
			: isNanoBananaPro
				? {
						title: t(
							"imageGenerator.upgradeModal.nanoBananaProTitle",
							{
								defaultValue: "Upgrade to Use Nano Banana Pro",
							},
						),
						description: t(
							"imageGenerator.upgradeModal.nanoBananaProDescription",
							{
								defaultValue:
									"Nano Banana Pro is a paid model. Subscribe to any plan or grab a one-time credit pack to unlock it, along with other premium features.",
							},
						),
					}
				: isWatermark
					? {
							title: t(
								"imageGenerator.upgradeModal.watermarkTitle",
								{
									defaultValue:
										"Remove the Watermark from Your Creations",
								},
							),
							description: t(
								"imageGenerator.upgradeModal.watermarkDescription",
								{
									defaultValue:
										"Watermark-free downloads are a paid feature. Subscribe to a plan or grab a one-time credit pack to remove the watermark and unlock other premium features.",
								},
							),
						}
					: isVideoResolution
						? {
								title: t(
									"imageGenerator.upgradeModal.videoResolutionTitle",
									{
										defaultValue:
											"Upgrade for Higher Resolution",
									},
								),
								description: t(
									"imageGenerator.upgradeModal.videoResolutionDescription",
									{
										defaultValue:
											"The smallest one-time credit pack unlocks 720p, and every larger pack unlocks 1080p and 4K too. On subscriptions, 720p needs Starter and above, and 1080p and 4K need Pro or Ultimate.",
									},
								),
							}
						: isImageResolution
							? {
									title: t(
										"imageGenerator.upgradeModal.imageResolutionTitle",
										{
											defaultValue:
												"Upgrade for Higher Resolution",
										},
									),
									description: t(
										"imageGenerator.upgradeModal.imageResolutionDescription",
										{
											defaultValue:
												"2K and 4K images are a paid feature. Subscribe to any plan or buy a one-time credit pack to unlock them.",
										},
									),
								}
							: isPremiumFeature
								? {
										title: t(
											"imageGenerator.upgradeModal.premiumFeatureTitle",
											{
												defaultValue:
													"Upgrade to Unlock This Feature",
											},
										),
										description: t(
											"imageGenerator.upgradeModal.premiumFeatureDescription",
											{
												defaultValue:
													"This is a premium feature. Subscribe to a paid plan or purchase a one-time credit pack to unlock it and other premium features.",
											},
										),
									}
								: {
										title: t(
											"imageGenerator.upgradeModal.title",
											{
												defaultValue:
													"Upgrade to Continue Creating",
											},
										),
										description: isRegionBlocked
											? t(
													"imageGenerator.upgradeModal.regionBlockedDescription",
													{
														defaultValue:
															"Free credits aren't available in your region yet. Grab a one-time credit pack or subscribe to start creating.",
													},
												)
											: t(
													"imageGenerator.upgradeModal.description",
													{
														defaultValue:
															"You've run out of credits. Grab a one-time credit pack or subscribe to keep creating.",
													},
												),
									};

	// How many credits this account has actually spent. Only shown on the
	// out-of-credits wall, and only when the credits query has answered —
	// getTotalCredits returns null while loading or on error, and inventing a
	// number there would put a wrong milestone in front of a paying user.
	const totalCredits = getTotalCredits("IMAGE_GENERATION");
	const usedCredits =
		totalCredits !== null
			? totalCredits - getRemainingCredits("IMAGE_GENERATION")
			: 0;
	const milestone =
		reason === "insufficient-credits" && usedCredits > 0
			? t("imageGenerator.upgradeModal.milestoneSubtitle", {
					credits: usedCredits,
					defaultValue:
						"You've already put {credits} credits to work 🎉 Keep the momentum going.",
				})
			: null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="max-w-6xl p-0 overflow-hidden bg-transparent border-none shadow-none"
				// 点遮罩不再关闭。这一批用户是全站最投入的（Clarity：页面数 2.2×、
				// 快速后退仅 1.75%），流失在「看了套餐没付」而不是「被弹窗烦到」，
				// 所以让套餐多停留一会儿。Esc 和右上角 X 都保留，不做无法关闭的牢笼。
				onPointerDownOutside={(e) => e.preventDefault()}
				// X 缩小、调淡：能找到、但不再是视线第一落点。
				// z-20 不能少：DialogHeader 带 z-10，而 DialogContent 在这里是
				// 透明壳、内层卡片铺满，X 会被标题区整块盖住点不动。叠加上面
				// 「点遮罩不关」，移动端（78% 流量）连 Esc 都没有，就成了关不掉
				// 的弹窗。
				// 图标小而淡，但按钮本身撑到 36px：低调说的是「不抢视线」，
				// 不是「点不中」。14px 的热区在手机上根本按不到。
				closeButtonClassName="top-1 right-1 z-20 flex size-9 items-center justify-center opacity-40 hover:opacity-80 text-muted-foreground [&>svg]:size-3.5"
			>
				<div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
					{/* 收紧的 pb / pt 必须写成 sm: 变体：`sm:p-6` 排在 `pb-2` 之后，
					    在 sm 以上断点会把它整个盖掉，于是标题区和滚动区各留 24px、
					    叠出 48px 空白。 */}
					<DialogHeader className="p-4 pb-2 sm:px-6 sm:pt-6 sm:pb-2 bg-card z-10">
						<DialogTitle className="text-xl sm:text-2xl font-bold text-center">
							{copy.title}
						</DialogTitle>
						<p className="text-center text-muted-foreground mt-2">
							{copy.description}
						</p>
						{milestone && (
							<p className="text-center text-sm font-medium text-primary mt-1">
								{milestone}
							</p>
						)}
					</DialogHeader>
					<div className="flex-1 overflow-y-auto p-4 pt-2 sm:px-6 sm:pb-6 sm:pt-2 custom-scrollbar">
						<PricingTable
							userId={user?.id}
							organizationId=""
							activePlanId={
								activePlan?.id !== "free"
									? activePlan?.id
									: undefined
							}
							className="w-full"
							hideFree
							compactMobile
							source="paywall-modal"
							reason={reason ?? "insufficient-credits"}
							// 所有弹窗墙首屏都停积分包（2026-10-10 老公定）。Waffo 9/2–10/10：订阅结账只有
							// 19% 付成（积分包 61%），没付成的 62 人里 58 个从没付过钱、不会回头买包；
							// 中国用户订阅结账 0/22（订阅没微信）。同日中午试过全停月付，看完数据当天撤回。
							// 包不可售时 PricingTable 自己退回月付
							defaultTab="onetime"
						/>
					</div>
					<div className="border-t border-border bg-card px-4 sm:px-6 py-3 flex items-center justify-center gap-2">
						{/* 微信只挂在积分包上（Waffo 的 CNY 一次性商品才出微信，
						    订阅出不了），所以这句只对简体中文讲——口径必须和
						    PricingTable 的微信按钮完全一致，否则繁中用户会看到
						    一句兑现不了的承诺。其余语言走通用信任文案。 */}
						{locale === "zh" ? (
							<>
								<WechatPayLogo className="h-5 w-5 shrink-0 text-[#07C160]" />
								<span className="text-xs sm:text-sm text-muted-foreground text-center">
									{t(
										"imageGenerator.upgradeModal.wechatHint",
									)}
								</span>
							</>
						) : (
							<>
								<ShieldCheckIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
								<span className="text-xs sm:text-sm text-muted-foreground text-center">
									{t(
										"imageGenerator.upgradeModal.secureHint",
										{
											defaultValue:
												"Secure checkout · Cancel anytime",
										},
									)}
								</span>
							</>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
